import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BuildIcon from '@mui/icons-material/Build';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Lightbulb from '@mui/icons-material/Lightbulb';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceIcon from '@mui/icons-material/Place';
import RadarIcon from '@mui/icons-material/Radar';
import { AppBar, Box, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Toolbar, useMediaQuery, type Theme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { drawerStateAtom } from '../atoms';
import { SimpleAlert } from '../hooks/useSimpleAlert';
import { useUser } from '../hooks/useUser';
import { Logo } from './Logo';
import { closedMixin, openedMixin } from './helpers';

type RouteItemProps = {
    text: string;
    icon: React.ReactNode;
    route: string;
    onClick?: () => void;
};

function RouteItem({ text, icon, route, onClick }: RouteItemProps) {
    const navigate = useNavigate();
    const location = useLocation();
    let selected = false;

    if (route === '/') {
        selected = location.pathname === '/';
    } else {
        selected = location.pathname.includes(route);
    }

    const handleClick = () => {
        navigate(route);
        if (onClick) onClick();
    };

    return (
        <ListItem disablePadding sx={{ display: 'block' }} onClick={handleClick}>
            <ListItemButton selected={selected} sx={{ px: 2.5 }}>
                <ListItemIcon sx={{ minWidth: 0, mr: 3 }}>{icon}</ListItemIcon>
                <ListItemText primary={text} />
            </ListItemButton>
        </ListItem>
    );
}

export const Layout = () => {
    const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
    const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
    const { data: user, isFetching } = useUser();
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const handleClose = () => {
        if (!isNotSm) setDrawerState(false);
    };

    const handleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (accountMenuAnchor) {
            setAccountMenuAnchor(null);
        } else {
            setAccountMenuAnchor(event.currentTarget);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('auwx');
        queryClient.invalidateQueries();
        navigate('/login');
    };

    if (isFetching || !user) return null;

    return (
        <>
            <Box sx={{ display: 'flex', minHeight: '100%', height: '100%' }}>
                <AppBar
                    position="fixed"
                    sx={(theme: Theme) =>
                        isNotSm
                            ? {
                                  zIndex: theme.zIndex.drawer + 1,
                              }
                            : {}
                    }>
                    <Toolbar sx={{ justifyContent: 'space-between' }}>
                        {!isNotSm && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={() => setDrawerState(true)}
                                edge="start"
                                sx={{
                                    marginRight: 1,
                                    ...(drawerState && { display: 'none' }),
                                }}>
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Logo width={100} height={45} />
                        <Box>
                            <IconButton size="large" onClick={handleAccountMenu} color="inherit">
                                <AccountCircleIcon fontSize="inherit" />
                            </IconButton>
                            <Menu anchorEl={accountMenuAnchor} keepMounted open={Boolean(accountMenuAnchor)} onClose={handleAccountMenu}>
                                <ListItem dense>{`${user.firstName} ${user.lastName}`}</ListItem>
                                <Divider />
                                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Drawer
                    variant={isNotSm ? 'permanent' : 'temporary'}
                    open={drawerState}
                    sx={(theme) => ({
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                        ...(drawerState && {
                            ...openedMixin(theme),
                            '& .MuiDrawer-paper': openedMixin(theme),
                        }),
                        ...(!drawerState && {
                            ...closedMixin(theme),
                            '& .MuiDrawer-paper': closedMixin(theme),
                        }),
                    })}>
                    {isNotSm && <Box sx={(theme) => theme.mixins.toolbar} />}
                    <List sx={{ p: 0 }}>
                        <RouteItem text="Stations" icon={<PlaceIcon />} route="/" onClick={handleClose} />
                        <RouteItem text="Alerts" icon={<NotificationsIcon />} route="/alerts" onClick={handleClose} />
                        <RouteItem text="Advisory" icon={<Lightbulb />} route="/advisory" onClick={handleClose} />
                    </List>
                    {user.role === 'admin' && <Divider />}
                    {user.role === 'admin' && (
                        <List
                            sx={{ p: 0 }}
                            subheader={
                                <>
                                    <ListSubheader sx={{ fontWeight: 'bold', textAlign: 'center', p: 0 }}>Admin</ListSubheader>
                                    <Divider />
                                </>
                            }>
                            <RouteItem text="Stations" icon={<RadarIcon />} route="/admin/stations" onClick={handleClose} />
                            <RouteItem text="Users" icon={<PeopleIcon />} route="/admin/users" onClick={handleClose} />
                            <RouteItem text="Applications" icon={<EditNoteIcon />} route="/admin/applications" onClick={handleClose} />
                            <RouteItem text="Configs" icon={<DataObjectIcon />} route="/admin/configs" onClick={handleClose} />
                            <Divider />
                            <ListSubheader sx={{ fontWeight: 'bold', textAlign: 'center', p: 0 }}>{`Max${drawerState ? ' Security' : ''}`}</ListSubheader>
                            <Divider />
                            <RouteItem text="Locations" icon={<LocationOnIcon />} route="/admin/max-locations" onClick={handleClose} />
                            <RouteItem text="Summaries" icon={<ListAltIcon />} route="/admin/max-summaries" onClick={handleClose} />
                        </List>
                    )}
                    <Divider />
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            p: theme.spacing(0, 1),
                            pr: 1.5,
                            ...theme.mixins.toolbar,
                        })}>
                        <IconButton onClick={() => setDrawerState(!drawerState)}>{drawerState ? <ChevronLeftIcon /> : <ChevronRightIcon />}</IconButton>
                    </Box>
                </Drawer>
                <Box component="main" sx={{ flex: '1 0', pt: isNotSm ? 8 : 7 }}>
                    <Outlet />
                </Box>
            </Box>
            <SimpleAlert />
        </>
    );
};
